/**
 * Hook React Query para Voice Commerce M6 — click-to-call desde WhatsApp.
 *
 * Wrappa la edge function call-center-start-call existente. NO duplica lógica
 * de Telnyx — solo reusa el flow click-to-call ya construido.
 */

import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';

export interface StartAgentCallInput {
  to_number: string;
  /** Optional para tracking: si la llamada se inicia desde una conv WA, propagar el ID. */
  source?: 'wa_chat_header' | 'wa_composer' | 'manual';
  source_reference_id?: string;
}

export interface StartAgentCallResponse {
  success: boolean;
  call_id?: string;
  provider_call_id?: string;
  message?: string;
  error?: string;
}

export function useStartAgentCall() {
  const { tenantId } = useAuth();

  return useMutation({
    mutationFn: async (input: StartAgentCallInput): Promise<StartAgentCallResponse> => {
      if (!tenantId) throw new Error('tenant_id required');
      if (!input.to_number) throw new Error('to_number required');

      const { data: session } = await supabase.auth.getSession();
      const accessToken = session?.session?.access_token;
      if (!accessToken) throw new Error('No active session');

      const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl;
      const url = `${supabaseUrl}/functions/v1/call-center-start-call`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          to_number: input.to_number,
        }),
      });

      const data = await res.json() as StartAgentCallResponse;
      if (!res.ok || !data.success) {
        throw new Error(data.error ?? data.message ?? `Error ${res.status}`);
      }
      return data;
    },
  });
}
